
import {
  reduce, keyBy, map, filter, keys, pick,
  indexOf, intersection, cloneDeep, findIndex,
  uniq, first, last, each, values, find, groupBy,
} from 'lodash';
import moment from 'moment';

import {
  CE_LOAD_ACCOUNTS,
  CE_RESET_SELECTED_ACCOUNTS,
  CE_LOAD_TRACKINGS,
  CE_SELECT_ACCOUNT,
  CE_UNSELECT_ACCOUNT,
  CE_TOGGLE_ALL_ACCOUNTS,
  CE_EDIT_ACCOUNT,
  CE_UPDATE_ACCOUNT,
  CE_EDIT_ACCOUNT_SELECT_CONTACTS,
  CE_EDIT_ACCOUNT_EDIT_CONTACT,
  CE_EDIT_ACCOUNT_UPDATE_CONTACT,
  CE_UPDATE_AMOUNT_RANGE,
  CE_SUBMIT_SAMPLES,
  CE_LOAD_CONTACT_DISPOSITIONS,
  CE_FETCH_SAVED_PARAMS,
  CE_ADD_SAVED_PARAMS,
  CE_FETCH_UNLINKED_CONTACTS,
  CE_REMOVE_SAVED_PARAMS,
  CE_FETCH_SUBMITTED_PARAMS,
  CE_MASS_FETCH_OPPORTUNITY_CONTACTS,
} from './CEActions';
import { NOMINATION_LOAD_CLIENT_INITIATIVES } from './NominationActions';

export const MASTER_CHECKBOX_STATUS = {
  none: 'none',
  full: 'full',
  partial: 'partial',
};

function selectAccount(account, savedAccounts = {}, tvProductMappings = {}) {
  const uniqRoles = values(keyBy(account.contacts, 'contact_id'));

  const selectedContacts = filter(uniqRoles, { isOpportunityContact: true }).map(contact => ({ ...contact }) /* copy */);
  const savedAccount = savedAccounts[account.account_id] || {};

  let selectedProductId = null;
  const selectedProductIds = [];
  if (account.productIds) {
    if (account.productIds.length === 1) {
      [selectedProductId] = account.productIds;
    }
    each(account.productIds, (productId) => {
      each(tvProductMappings[productId], (tvProductId) => {
        selectedProductIds.push(tvProductId);
      });
    });
  }

  const { tracking } = account;
  // if tracking is available, and remove contacts that were submmitted from saved params
  if (tracking && savedAccount.selected_contacts) {
    const submmittedIds = tracking.submittedContactIds;
    savedAccount.selected_contacts = reduce(savedAccount.selected_contacts, (contacts, contact) => {
      if (indexOf(submmittedIds, contact.contact_id) < 0) {
        contacts.push(contact);
      }
      return contacts;
    }, []);
  }

  return {
    ...account,
    selected_contacts: selectedContacts,
    selected_product_id: selectedProductId,
    selected_product_ids: selectedProductIds,
    ...savedAccount,
    account: {
      ...pick(account, ['account_id', 'name', 'annual_revenue', 'number_of_employees', 'owner_id', 'phone', 'website', 'industry']),
      ...(savedAccount.account || {}),
    },
  };
}

// Initial State
const initialState = {
  accounts: {
    accounts: [],
    indexedContacts: {},
    status: null,
    errors: null,
  },
  amounts: {
    type: 'range',
    values: [0, 0],
    tree: [],
    selected: [0, 0],
  },
  selectedAccounts: {},
  editingAccount: null,
  trackings: {
    trackings: {}, // indexed
    status: null,
    errors: null,
  },
  savedParams: {
    accounts: {}, // indexed
    total: 0,
    status: null,
    errors: null,
  },
  submittedParams: {
    accounts: {}, // indexed
    total: 0,
    status: null,
    errors: null,
  },
  unlinkedContacts: {
    contacts: [],
    status: null,
    errors: null,
  },
};
const CEReducer = (state = initialState, action) => {
  switch (action.type) {
    case CE_RESET_SELECTED_ACCOUNTS: {
      return { ...state, selectedAccounts: {}, editingAccount: null };
    }
    case CE_LOAD_ACCOUNTS: {
      const { amounts } = action;
      const newAmounts = {
        type: 'range',
        values: [0, 0],
        tree: [],
        selected: [0, 0],
      };
      if (amounts.length) {
        newAmounts.tree = amounts;
        const small = first(amounts);
        const big = last(amounts);
        newAmounts.selected = [small, big];
        newAmounts.values = [small, big];
      }
      return {
        ...state,
        accounts: {
          accounts: action.accounts,
          indexedContacts: action.indexedContacts,
          status: action.status,
          errors: action.errors,
        },
        amounts: newAmounts,
      };
    }
    case NOMINATION_LOAD_CLIENT_INITIATIVES: {
      const { selectedAccounts } = state;
      const newSelectedAccounts = reduce(selectedAccounts, (results, account) => {
        results[account.account_id] = {
          ...account,
          selected_contacts: reduce(account.selected_contacts, (contacts, contact) => {
            contacts.push({
              ...contact, tags: [], buyer_type: null, buyer_role: null,
            });
            return contacts;
          }, []),
        };
        // todo: reset products
        return results;
      }, {});
      return { ...state, selectedAccounts: newSelectedAccounts };
    }
    case CE_SELECT_ACCOUNT: {
      const { selectedAccounts, savedParams } = state;
      const selected = selectedAccounts[action.account.account_id];
      if (!selected) {
        const acc = find(state.accounts, { account_id: action.account.account_id }) || action.account;
        const newParams = selectAccount(acc, savedParams.accounts, action.tvProductMappings);
        return { ...state, selectedAccounts: { ...selectedAccounts, [action.account.account_id]: newParams } };
      }
      return state;
    }
    case CE_UNSELECT_ACCOUNT: {
      const { selectedAccounts, editingAccount } = state;
      const newSelectedAccounts = { ...selectedAccounts };
      delete newSelectedAccounts[action.account.account_id];
      let newEditingAccount = editingAccount;
      if (editingAccount) {
        if (editingAccount.account_id === action.account.account_id) {
          newEditingAccount = null;
        }
      }
      return { ...state, selectedAccounts: newSelectedAccounts, editingAccount: newEditingAccount };
    }
    case CE_LOAD_TRACKINGS: {
      const { trackings } = action;
      const loaded = action.status === 'loaded';
      const { accounts, selectedAccounts } = state;
      const hashedTrackings = {};
      const newAccounts = accounts.accounts.map((account) => {
        let tracking = trackings[account.account_id];
        let submitted = false;
        if (tracking) {
          let lastActivity = null;
          let contactIds = [];
          tracking.trackings.map((t) => {
            contactIds = reduce(t.contacts, (results, contact) => {
              if (contact.lastActivity) {
                const current = moment(contact.lastActivity.completed);
                if (!lastActivity || lastActivity.completed.isBefore(current)) {
                  lastActivity = { ...contact.lastActivity, completed: current };
                }
              }
              results.push(contact.sfa__contactId);
              return results;
            }, contactIds);
            return t;
          });
          tracking.submittedContactIds = uniq(contactIds);
          if (!submitted) {
            submitted = tracking.submittedContactIds.length > 0;
          }
          tracking.lastActivity = lastActivity;
        } else if (loaded) {
          tracking = {};
        }
        hashedTrackings[account.account_id] = tracking;
        return { ...account, submitted, tracking };
      });

      const newSelectedAccounts = reduce(selectedAccounts, (results, account) => {
        results[account.account_id] = { ...account, tracking: hashedTrackings[account.account_id] };
        return results;
      }, {});

      return {
        ...state,
        trackings: {
          trackings: action.trackings,
          status: action.status,
          errors: action.errors,
        },
        accounts: {
          ...accounts,
          accounts: newAccounts,
        },
        editingAccount: null, // to simplyfy things
        selectedAccounts: newSelectedAccounts,
      };
    }
    case CE_LOAD_CONTACT_DISPOSITIONS: {
      const { accounts, selectedAccounts } = state;
      const {
        status, activities, errors, opportunityId, contactId,
      } = action;
      const hashedTrackings = {};
      const newAccounts = accounts.accounts.map((account) => {
        const { tracking } = account;
        if (tracking) {
          const newTracking = {
            ...tracking,
            trackings: tracking.trackings ? tracking.trackings.map((t) => {
              if (opportunityId === t.tv__opportunityId) {
                return {
                  ...t,
                  contacts: t.contacts ? t.contacts.map((contact) => {
                    if (contact.tv__contactId === contactId) {
                      return { ...contact, activities: { activities, errors, status } };
                    }
                    return contact;
                  }) : [],
                };
              }
              return t;
            }) : [],
          };
          hashedTrackings[account.account_id] = newTracking;
          return { ...account, tracking: newTracking };
        }
        return account;
      });

      const newSelectedAccounts = reduce(selectedAccounts, (results, account) => {
        results[account.account_id] = { ...account, tracking: hashedTrackings[account.account_id] || account.tracking };
        return results;
      }, {});
      return { ...state, accounts: { ...accounts, accounts: newAccounts }, selectedAccounts: newSelectedAccounts };
    }
    case CE_TOGGLE_ALL_ACCOUNTS: {
      const { savedOnly, tvProductMappings } = action;
      const {
        selectedAccounts, accounts, editingAccount, savedParams,
      } = state;
      let selectedIds = [];
      let availableAccounts = {};
      if (savedOnly) {
        const savedAccounts = savedParams ? savedParams.accounts : {};
        selectedIds = reduce(selectedAccounts, (items, wl, oId) => {
          if (savedAccounts[oId]) {
            items.push(oId);
          }
          return items;
        }, []);
        availableAccounts = reduce(accounts.accounts, (items, account) => {
          const oId = account.account_id;
          if (savedAccounts[oId]) {
            items[oId] = account;
          }
          return items;
        }, {});
      } else {
        selectedIds = keys(selectedAccounts);
        availableAccounts = keyBy(accounts.accounts, 'account_id');
      }

      const availableIds = map(availableAccounts, 'account_id');
      const validSelectedIds = intersection(selectedIds, availableIds);

      let newSelectedAccounts = selectedAccounts;
      let newEditingAccount = editingAccount;

      if (validSelectedIds.length < availableIds.length) {
        // should select all
        newSelectedAccounts = reduce(availableIds, (results, id) => {
          if (selectedAccounts[id]) {
            results[id] = selectedAccounts[id];
          } else {
            results[id] = selectAccount(availableAccounts[id], savedParams, tvProductMappings);
          }
          return results;
        }, {});
      } else {
        // should UNselecte all
        newSelectedAccounts = {};
        newEditingAccount = null;
      }
      return { ...state, selectedAccounts: newSelectedAccounts, editingAccount: newEditingAccount };
    }
    case CE_EDIT_ACCOUNT: {
      const { editingAccount, selectedAccounts } = state;
      if (editingAccount && editingAccount.account_id === action.account.account_id) {
        return { ...state, editingAccount: null };
      } else if (selectedAccounts[action.account.account_id]) {
        return { ...state, editingAccount: selectedAccounts[action.account.account_id] };
      }
      return state;
    }
    case CE_UPDATE_ACCOUNT: {
      const { selectedAccounts } = state;
      const selected = selectedAccounts[action.account.account_id] || {};
      const newParams = cloneDeep({ ...selected, ...action.account });
      return { ...state, selectedAccounts: { ...selectedAccounts, [action.account.account_id]: newParams } };
    }
    case CE_EDIT_ACCOUNT_SELECT_CONTACTS: {
      const { editingAccount } = state;
      if (editingAccount) {
        const { contacts } = editingAccount;
        const selectedContacts = editingAccount.selected_contacts;
        const indexedSelectedContacts = keyBy(selectedContacts, 'contact_id');
        const requestedContacts = action.contacts;
        const availableContacts = keyBy(contacts, 'contact_id');
        const newSelectedContacts = reduce(requestedContacts, (results, contact) => {
          if (availableContacts[contact.contact_id]) {
            if (indexedSelectedContacts[contact.contact_id]) {
              results.push(indexedSelectedContacts[contact.contact_id]);
            } else {
              results.push({ ...contact });
            }
          }
          return results;
        }, []);
        return { ...state, editingAccount: { ...editingAccount, selected_contacts: newSelectedContacts } };
      }
      return state;
    }

    case CE_EDIT_ACCOUNT_EDIT_CONTACT: {
      const { editingAccount } = state;
      if (editingAccount) {
        if (action.contact) {
          const selectedContacts = editingAccount.selected_contacts;
          if (selectedContacts) {
            const idx = findIndex(selectedContacts, { contact_id: action.contact.contact_id });
            if (idx >= 0) {
              return { ...state, editingAccount: { ...editingAccount, editingContact: { ...action.contact } } };
            }
          }
        } else {
          return { ...state, editingAccount: { ...editingAccount, editingContact: null } };
        }
      }
      return state;
    }
    case CE_EDIT_ACCOUNT_UPDATE_CONTACT: {
      const { editingAccount } = state;
      if (editingAccount) {
        if (action.contact) {
          const selectedContacts = editingAccount.selected_contacts;
          if (selectedContacts) {
            const attrs = {};
            const fields = ['selected_contacts', 'accountContacts', 'contacts'];
            each(fields, (field) => {
              let newArray = editingAccount[field] || [];
              const idx = findIndex(newArray, { contact_id: action.contact.contact_id });
              if (idx >= 0) {
                newArray = [...newArray];
                newArray[idx] = { ...action.contact };
                attrs[field] = newArray;
              }
            });

            return {
              ...state,
              editingAccount: {
                ...editingAccount,
                ...attrs,
              },
            };
          }
        } else {
          return { ...state, editingAccount: { ...editingAccount, editingContact: null } };
        }
      }
      return state;
    }
    case CE_UPDATE_AMOUNT_RANGE: {
      const { amounts } = state;
      const newAmounts = { ...amounts, selected: action.range };
      return { ...state, amounts: newAmounts };
    }
    case CE_SUBMIT_SAMPLES: {
      const results = {};
      action.addedSample.map((sample) => {
        if (!results[sample.opportunityId]) {
          results[sample.opportunityId] = [];
        }
        results[sample.opportunityId].push(sample.contactId);
        return sample;
      });
      const { selectedAccounts } = state;
      const newSelectedAccounts = reduce(selectedAccounts, (accounts, account, accountId) => {
        const submittedContactIds = results[accountId];
        if (submittedContactIds) {
          const newSelectedContacts = reduce(account.selected_contacts, (selectedContacts, contact) => {
            if (indexOf(submittedContactIds, contact.contact_id) < 0) {
              selectedContacts.push(contact);
            }
            return selectedContacts;
          }, []);
          if (newSelectedContacts.length > 0) {
            accounts[accountId] = { ...account, selected_contacts: newSelectedContacts };
          }
        } else {
          accounts[accountId] = account;
        }
        return accounts;
      }, {});
      return { ...state, selectedAccounts: newSelectedAccounts };
    }
    case CE_FETCH_SAVED_PARAMS: {
      const { accounts, status, errors } = action;

      const indexedAccounts = reduce(accounts, (items, account) => {
        items[account.account_id] = account;
        return items;
      }, {});

      const allAccounts = reduce(state.accounts.accounts, (items, account) => {
        account.is_saved = indexedAccounts[account.account_id] !== undefined;
        items.push(account);
        return items;
      }, []);
      const newCEAccounts = reduce(state.selectedAccounts, (items, account) => {
        account.is_saved = indexedAccounts[account.account_id] !== undefined;
        items[account.account_id] = account;
        return items;
      }, {});

      return {
        ...state,
        accounts: {
          ...state.accounts,
          accounts: allAccounts,
        },
        selectedAccounts: newCEAccounts,
        savedParams: {
          accounts: indexedAccounts, // indexed
          total: accounts.length,
          status,
          errors,
        },
      };
    }
    case CE_FETCH_SUBMITTED_PARAMS: {
      const { accounts, status, errors } = action;

      const indexedAccounts = reduce(accounts, (items, account) => {
        if (!items[account.account_id]) {
          items[account.account_id] = [];
        }
        items[account.account_id].push(account);
        return items;
      }, {});

      const allAccounts = reduce(state.accounts.accounts, (items, account) => {
        account.has_submitted_data = indexedAccounts[account.account_id] !== undefined;
        items.push(account);
        return items;
      }, []);
      const newCEAccounts = reduce(state.selectedAccounts, (items, account) => {
        account.has_submitted_data = indexedAccounts[account.account_id] !== undefined;
        items[account.account_id] = account;
        return items;
      }, {});

      return {
        ...state,
        accounts: {
          ...state.accounts,
          accounts: allAccounts,
        },
        selectedAccounts: newCEAccounts,
        submittedParams: {
          accounts: indexedAccounts, // indexed
          total: accounts.length,
          status,
          errors,
        },
      };
    }
    case CE_ADD_SAVED_PARAMS: {
      const { accounts } = action;
      const newAccounts = {
        ...state.savedParams.accounts,
      };
      each(accounts, (account) => {
        const id = account.account_id;
        newAccounts[id] = account;
      });

      const allAccounts = reduce(state.accounts.accounts, (items, account) => {
        account.is_saved = newAccounts[account.account_id] !== undefined;
        items.push(account);
        return items;
      }, []);
      const newCEAccounts = reduce(state.selectedAccounts, (items, account) => {
        account.is_saved = newAccounts[account.account_id] !== undefined;
        items[account.account_id] = account;
        return items;
      }, {});

      const total = values(newAccounts).length;
      return {
        ...state,
        accounts: {
          ...state.accounts,
          accounts: allAccounts,
        },
        selectedAccounts: newCEAccounts,
        savedParams: {
          ...state.savedParams,
          total,
          accounts: newAccounts, // indexed
        },
      };
    }
    case CE_REMOVE_SAVED_PARAMS: {
      const { ids } = action;
      const newAccounts = {
        ...state.savedParams.accounts,
      };
      each(ids, (id) => {
        delete newAccounts[id];
      });

      const allAccounts = reduce(state.accounts.accounts, (items, account) => {
        account.is_saved = newAccounts[account.account_id] !== undefined;
        items.push(account);
        return items;
      }, []);
      const newCEAccounts = reduce(state.selectedAccounts, (items, account) => {
        account.is_saved = newAccounts[account.account_id] !== undefined;
        if (newAccounts[account.account_id] !== undefined) {
          items[account.account_id] = account;
        }
        return items;
      }, {});

      const total = values(newAccounts).length;
      return {
        ...state,
        accounts: {
          ...state.accounts,
          accounts: allAccounts,
        },
        selectedAccounts: newCEAccounts,
        savedParams: {
          ...state.savedParams,
          total,
          accounts: newAccounts, // indexed
        },
      };
    }
    case CE_FETCH_UNLINKED_CONTACTS: {
      const { contacts, status, errors } = action;
      return {
        ...state,
        unlinkedContacts: {
          contacts, status, errors,
        },
      };
    }
    case CE_MASS_FETCH_OPPORTUNITY_CONTACTS: {
      const { contacts, opportunityIds, status } = action;
      const indexedByContactId = keyBy(contacts, 'contact_id');
      const indexedByAccountId = groupBy(contacts, 'account_id');

      const targetAccounts = filter(state.accounts.accounts, account => intersection(opportunityIds, account.opportunityIds).length > 0);
      each(targetAccounts, (targetAccount) => {
        targetAccount.contactStatus = status;
        if (contacts.length) {
          const accountContacts = indexedByAccountId[targetAccount.account_id] || targetAccount.accountContacts;

          const opportunityContacts = reduce(targetAccount.opportunityContactIds, (items, opportunityContactId) => {
            if (indexedByContactId[opportunityContactId]) {
              const newContact = { ...indexedByContactId[opportunityContactId], isOpportunityContact: true };
              if (targetAccount.roles && targetAccount.roles[opportunityContactId]) {
                newContact.role = targetAccount.roles[opportunityContactId];
              }
              items.push(newContact);
            }
            return items;
          }, []);

          targetAccount.opportunityContacts = opportunityContacts;

          const indexedOpportunityContacts = keyBy(targetAccount.opportunityContacts, 'contact_id');


          targetAccount.accountContacts = reduce(accountContacts, (items, contact) => {
            if (!indexedOpportunityContacts[contact.contact_id]) {
              items.push(contact);
            }
            return items;
          }, []);


          const allContacts = [...targetAccount.accountContacts, ...targetAccount.opportunityContacts];
          
          targetAccount.contacts = allContacts;
          targetAccount.indexedContacts = keyBy(allContacts, 'contact_id');
        }
      });
      const targetIndexedAccounts = keyBy(targetAccounts, 'account_id');

      const accounts = reduce(state.accounts.accounts, (items, account) => {
        if (targetIndexedAccounts[account.account_id]) {
          items.push(targetIndexedAccounts[account.account_id]);
        } else {
          items.push(account);
        }
        return items;
      }, []);


      return {
        ...state,
        accounts: {
          ...state.accounts,
          accounts,
          indexedContacts: {
            ...state.accounts.indexedContacts,
            ...indexedByContactId,
          },
        },
      };
    }
    default:
      return state;
  }
};

export const getAccounts = state => state.ceNomination.accounts.accounts;
export const getIndexedContacts = state => state.ceNomination.accounts.indexedContacts;
export const getAccountLoading = state => state.ceNomination.accounts.status === 'loading';
export const getAccountLoaded = state => state.ceNomination.accounts.status === 'loaded';
export const getAccountHasErrors = state => state.ceNomination.accounts.status === 'error';
export const getAccountErrors = (state) => {
  if (state.ceNomination.accounts.status === 'error') {
    return state.ceNomination.accounts.errors;
  }
  return null;
};

export const getTrackingsLoading = (state) => {
  const { trackings } = state.ceNomination;
  return trackings.status === 'loading';
};
export const getTrackingsLoaded = (state) => {
  const { trackings } = state.ceNomination;
  return trackings.status === 'loaded';
};

export const getSelectedAccounts = state => state.ceNomination.selectedAccounts;

export const getMasterCheckboxStatus = (state) => {
  const { selectedAccounts, accounts } = state.ceNomination;

  const selectedIds = [];
  const selectedSavedIds = [];

  each(selectedAccounts, (acc, oId) => {
    selectedIds.push(oId);
    if (acc.is_saved) {
      selectedSavedIds.push(oId);
    }
  });

  const availableIds = [];
  const availableSavedIds = [];
  each(accounts.accounts, (acc) => {
    const oId = acc.account_id;
    availableIds.push(oId);
    if (acc.is_saved) {
      availableSavedIds.push(oId);
    }
  });

  const validSelectedIds = intersection(selectedIds, availableIds);
  const validSelectedSavedIds = intersection(selectedSavedIds, availableSavedIds);

  let allStatus = MASTER_CHECKBOX_STATUS.none;
  let savedStatus = MASTER_CHECKBOX_STATUS.none;

  if (validSelectedIds.length === 0) {
    allStatus = MASTER_CHECKBOX_STATUS.none;
  } else if (validSelectedIds.length === availableIds.length) {
    allStatus = MASTER_CHECKBOX_STATUS.full;
  } else {
    allStatus = MASTER_CHECKBOX_STATUS.partial;
  }

  if (validSelectedSavedIds.length === 0) {
    savedStatus = MASTER_CHECKBOX_STATUS.none;
  } else if (validSelectedSavedIds.length === availableSavedIds.length) {
    savedStatus = MASTER_CHECKBOX_STATUS.full;
  } else {
    savedStatus = MASTER_CHECKBOX_STATUS.partial;
  }
  return { allStatus, savedStatus };
};

export const getEditingAccount = state => state.ceNomination.editingAccount;
export const getEditingAccountEditingContact = (state) => {
  const editingAccount = getEditingAccount(state);
  if (editingAccount && editingAccount.editingContact) {
    return editingAccount.editingContact;
  }
  return null;
};
export const getAmounts = state => state.ceNomination.amounts;

export const getSavedParams = (state) => {
  const { savedParams } = state.ceNomination;
  return savedParams ? savedParams.accounts : [];
};

export const getSavedParamsLoading = (state) => {
  const { savedParams } = state.ceNomination;
  return savedParams && savedParams.status === 'loading';
};
export const getSavedParamsLoaded = (state) => {
  const { savedParams } = state.ceNomination;
  return savedParams && savedParams.status === 'loaded';
};
export const getTotalSaved = (state) => {
  const { savedParams } = state.ceNomination;
  return savedParams ? savedParams.total : 0;
};

export const getSubmittedParams = (state, accountId) => {
  const { submittedParams } = state.ceNomination;
  return submittedParams ? submittedParams.accounts[accountId] || [] : [];
};

export const getUnlinkedContactLoading = (state) => {
  const { unlinkedContacts } = state.ceNomination;
  return unlinkedContacts && unlinkedContacts.status === 'loading';
};
export const getUnlinkedContactLoaded = (state) => {
  const { unlinkedContacts } = state.ceNomination;
  return unlinkedContacts && unlinkedContacts.status === 'loaded';
};
export const getUnlinkedContacts = (state) => {
  const { unlinkedContacts } = state.ceNomination;
  return unlinkedContacts ? unlinkedContacts.contacts : [];
};

export const getSubmitParams = (state) => {
  let contactIds = [];
  let salesReps = [];
  let allSelectedProductIds = [];
  let invalidAccounts = 0;
  let invalidSavedAccounts = 0;
  const savedParams = [];
  let savedContactIds = [];
  let savedSalesReps = [];
  let savedSelectedProductIds = [];
  const selected = []; // all, include invalid
  const savedSelected = []; // all saved, include invalid

  const params = reduce(state.ceNomination.selectedAccounts, (accounts, account) => {
    // const selectedProductId = account.selected_product_id;
    const selectedProductIds = account.selected_product_ids;
    const isSaved = account.is_saved === true;
    selected.push(account);
    if (isSaved) {
      savedSelected.push(account);
    }
    if (selectedProductIds && selectedProductIds.length) {
      const contacts = reduce(account.selected_contacts, (results, contact) => {
        const newContact = cloneDeep(contact);
        contactIds.push(contact.contact_id);
        results.push(newContact);
        if (isSaved) {
          savedContactIds.push(contact.contact_id);
        }
        return results;
      }, []);
      if (contacts.length) {
        const newAccount = cloneDeep(account);
        delete newAccount.accountContacts;
        delete newAccount.annual_revenue; // real value is in newAccount.account.annual_revenue
        delete newAccount.number_of_employees; // real value is in newAccount.account.number_of_employees
        delete newAccount.industry; // real value is in newAccount.account.industry
        delete newAccount.website; // real value is in newAccount.account.industry
        delete newAccount.phone; // real value is in newAccount.account.phone
        delete newAccount.name; // real value is in newAccount.account.name
        // newAccount.name = `State of Experience for ${account.name} in ${moment().format('MMMM')}`; // generate this in the BE
        delete newAccount.contacts;
        delete newAccount.indexedContacts;
        delete newAccount.is_saved;
        delete newAccount.opportunityContactIds;
        delete newAccount.opportunityContacts;
        delete newAccount.roles;
        delete newAccount.submitted;
        delete newAccount.tracking;
        delete newAccount.totalOpportunities;
        delete newAccount.productIds;
        delete newAccount.selected_product_id;

        newAccount.selected_product_ids = selectedProductIds;
        newAccount.product = null; // get from FE when submmited
        newAccount.selected_contacts = contacts;

        salesReps.push(account.owner_id);
        allSelectedProductIds = allSelectedProductIds.concat(selectedProductIds);
        accounts.push(newAccount);

        if (isSaved) {
          savedSalesReps.push(account.owner_id);
          savedParams.push(newAccount);
          savedSelectedProductIds = savedSelectedProductIds.concat(selectedProductIds);
        }
      } else {
        invalidAccounts += 1;
        if (isSaved) {
          invalidSavedAccounts += 1;
        }
      }
    } else {
      invalidAccounts += 1;
      if (isSaved) {
        invalidSavedAccounts += 1;
      }
    }
    return accounts;
  }, []);
  contactIds = uniq(contactIds);
  salesReps = uniq(salesReps);
  allSelectedProductIds = uniq(allSelectedProductIds);

  savedContactIds = uniq(savedContactIds);
  savedSalesReps = uniq(savedSalesReps);
  savedSelectedProductIds = uniq(savedSelectedProductIds);
  return {
    params,
    savedParams,
    stats: {
      contactIds,
      salesReps,
      selectedProductIds: allSelectedProductIds,
      invalidAccounts,
      selected,
    },
    savedStats: {
      contactIds: savedContactIds,
      salesReps: savedSalesReps,
      selectedProductIds: savedSelectedProductIds,
      invalidAccounts: invalidSavedAccounts,
      selected: savedSelected,
    },
  };
};

export default CEReducer;
